import React, { useEffect, useRef, useState } from 'react';
import styles from './LoginPanel.module.scss';
import { getQueryParamNamed } from '../../../SharedLib/Util/QueryParamsUtil';
import { cacheRedirectUrl, getSanitizedRedirectUrl, tweakRedirectUrlOnSuccessfulSignup } from '../../../SharedLib/Util/RedirectUtil';
import { authUtil } from '../../../SharedLib/Util/AuthUtil';
import { CognitoHostedUIIdentityProvider } from '../../../SharedLib/Util/AmplifyAuth';
import SignUpForm from '../SignUpForm/SignUpForm';
import LoginForm from '../LoginForm/LoginForm';
import ResetPasswordForm from '../ResetPasswordForm/ResetPasswordForm';
import GitHubIcon from '../../Icon/GitHubIcon';
import AppleIcon from '../../Icon/AppleIcon';
import SignupVerifyEmail from '../SignUpVerifyEmail/SignUpVerifyEmail';
import { useHistory, useLocation } from 'react-router-dom';
import W3LogoButton from '../../../SharedLib/Component/Buttons/W3LogoButton/W3LogoButton';
import { X } from 'react-bootstrap-icons';
import GoogleIconColored from '../../Icon/GoogleIconColored';
import FeideIconColored from '../../Icon/FeideIconColored';
import FacebookIconColored from '../../Icon/FacebookIconColored';
import ClassLinkIconColored from '../../Icon/ClassLinkIconColored';
import { isCustomSocialAuthEnabled } from '../../CustomSocialAuth/CustomSocialAuthUtil';
import { getClassicUrl, getProfileUrl } from '../../../SharedLib/Util/EnvironmentUtil';
import ChangePasswordForm from '../ChangePasswordForm/ChangePasswordForm';
import { Overlay, Popover } from 'react-bootstrap';
import ConfirmWithNewPasswordForm from '../ConfirmWithNewPasswordForm/ConfirmWithNewPasswordForm';

export enum LoginView {
  LOGIN_SIGNUP = 'login_signup',
  LOGIN = 'login',
  SIGNUP = 'signup',
  RESET_PASSWORD = 'reset_password',
  VERIFICATION_PENDING = 'verification_pending',
  RESET = 'reset',
  CONFIRM_NEW_PASSWORD = 'confirm_new_password',
}

export interface ILoginPanelProps {
  className?: string;
}

const SOCIAL_BUTTON_DISPLAY_NAMES: Record<string, string> = {
  Github: 'GitHub',
};

/** UI label → lowercase `provider` for `/social-auth?init=true&provider=`. */
const SOCIAL_BUTTON_TO_AUTHORIZE_PROVIDER: Record<string, string> = {
  Google: 'google',
  Facebook: 'facebook',
  Apple: 'signinwithapple',
  Github: 'github',
  Classlink: 'classlink',
  Feide: 'feide',
};

export const getViewFromPathname = (pathname: string) => {
  switch (pathname) {
    case '/reset-password': // anonymous reset password
      return LoginView.RESET_PASSWORD;
    case '/reset': // reset from email link. May be logged in
      return LoginView.RESET;
    case '/sign-up':
      return LoginView.SIGNUP;
    case '/signup':
      return LoginView.SIGNUP;
    case '/confirm-new_password':
      return LoginView.CONFIRM_NEW_PASSWORD;
    default:
      return LoginView.LOGIN;
  }
};

const LoginPanel: React.FC<ILoginPanelProps> = () => {
  const socialLoginProviders = ['Google', 'Facebook', 'Apple', 'Github', 'Feide'];

  const location = useLocation();
  const [view, setView] = useState<LoginView>(getViewFromPathname(location.pathname));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [xButtonUrl, setXButtonUrl] = useState(getClassicUrl());
  const [showFeideTooltip, setShowFeideTooltip] = useState(false);
  const feideButtonRef = useRef<HTMLButtonElement>(null);
  const feidePopperRef = useRef<{ scheduleUpdate?: () => void } | null>(null);
  const updateFeideTooltip = () => {
    window.requestAnimationFrame(() => feidePopperRef.current?.scheduleUpdate?.());
  };
  const history = useHistory();
  const providerLogoMap: Record<string, JSX.Element> = {
    Google: <GoogleIconColored width="24" height="24" viewBox="16 16 24 24" />,
    Facebook: <FacebookIconColored width="24" height="24" viewBox="16 16 24 24" />,
    Apple: <AppleIcon width="24" height="24" viewBox="16 16 24 24" />,
    Github: <GitHubIcon width="24" height="24" viewBox="16 16 24 24" />,
    Feide: <FeideIconColored width="24" height="24" viewBox="16 16 24 24" />,
    Classlink: <ClassLinkIconColored width="24" height="24" viewBox="16 16 24 24" />,
  };


  useEffect(() => {
    const _xButtonUrl = getSanitizedRedirectUrl({
      skipOauthRedirectLookup: true,
      loggedOut: true,
      fallback: getClassicUrl(),
    });

    if (_xButtonUrl) {
      setXButtonUrl(_xButtonUrl);
    }
  }, []);

  useEffect(() => {
    if (view !== LoginView.VERIFICATION_PENDING) {
      history.push(`/${view.toLowerCase().replace('_', '-')}` + history.location.search);
    }
  }, [view, history]);

  useEffect(() => {
    setView(getViewFromPathname(location.pathname));
  }, [location.pathname]);

  async function handleSocialLoginClick(provider: CognitoHostedUIIdentityProvider | string) {
    if (isCustomSocialAuthEnabled()) {
      const authorizeProvider = typeof provider === 'string' ? SOCIAL_BUTTON_TO_AUTHORIZE_PROVIDER[provider] : undefined;

      if (!authorizeProvider) {
        await federatedLogin(provider);
        return;
      }

      cacheRedirectUrl();
      window.location.href = `${getProfileUrl()}/social-auth?init=true&provider=${encodeURIComponent(authorizeProvider)}`;
      return;
    }

    await federatedLogin(provider);
  }

  async function federatedLogin(provider: CognitoHostedUIIdentityProvider | string) {
    // Store authorization_code, redirect_uri and state in session storage here. To be retrieved later in handleLoggedIn when directed back to profile after social login.
    sessionStorage.clear();
    const auth_code = getQueryParamNamed('authorization_code');
    const redirect_uri = getQueryParamNamed('redirect_uri');

    if (auth_code !== undefined) {
      sessionStorage.setItem('authorization_code', auth_code.value);

      const auth_state = getQueryParamNamed('state');

      if (auth_state !== undefined) {
        sessionStorage.setItem('state', auth_state.value);
      }
    }

    if (redirect_uri !== undefined) {
      sessionStorage.setItem('redirect_uri', redirect_uri.value);
    }

    cacheRedirectUrl();

    const federatedLoginRes = await authUtil.federatedLogin(provider);

    if (federatedLoginRes.status === 'loggedin') {
      tweakRedirectUrlOnSuccessfulSignup(); // TODO: (mid) find a way to distinguish between signup and login
    }
  }

  const tabChangeText = () => {
    if (view === LoginView.SIGNUP) {
      return (
        <div className={styles.header_login_text}>
          Already have an account?{' '}
          <span className={styles.login} onClick={() => setView(LoginView.LOGIN)}>
            Sign in
          </span>
        </div>
      );
    }

    if (view === LoginView.LOGIN) {
      return (
        <div className={styles.header_login_text}>
          Don't have an account?{' '}
          <span className={styles.login} onClick={() => setView(LoginView.SIGNUP)}>
            Register
          </span>
        </div>
      );
    }
  };

  const headingText = () => {
    if (view === LoginView.SIGNUP) {
      return <h2 className={styles.heading_text}>Create your account</h2>;
    }

    if (view === LoginView.LOGIN) {
      return (
        <div className={styles.heading}>
          <h2 className={styles.heading_text}>Sign in</h2>
          <div className={styles.subheading_text}>Get access to more learning features</div>
        </div>
      );
    }
  };

  return (
    <div className={styles.login_panel}>
      <div className={styles.login_signup_container}>
        <div
          className={styles.login_signup}
          style={{
            gridTemplateRows:
              view === LoginView.RESET_PASSWORD || view === LoginView.VERIFICATION_PENDING ? 'auto' : '60px 40px 120px auto',
          }}
        >
          <div className={styles.form_header}>
            <a href={xButtonUrl} className={styles.x_button}>
              <X size={'30'} />
            </a>
          </div>
          <div className={styles.form_header_mobile}>
            <W3LogoButton loggedIn={false} width="40" height="36" />
            <a href={xButtonUrl} className={styles.x_button}>
              <X size={'30'} />
            </a>
          </div>
          {(view === LoginView.LOGIN || view === LoginView.SIGNUP) && (
            <div className={styles.heading_container}>
              {headingText()}
              {tabChangeText()}
            </div>
          )}
          {(view === LoginView.LOGIN || view === LoginView.SIGNUP) && (
            <div className={styles.social_login_container}>
              <>
                <div className={styles.social_login_buttons}>
                  {socialLoginProviders.map((provider, index) =>
                    provider === 'Feide' ? (
                      <React.Fragment key={index}>
                        <Overlay
                          show={showFeideTooltip}
                          target={feideButtonRef}
                          placement="bottom"
                          onEnter={updateFeideTooltip}
                          onEntering={updateFeideTooltip}
                          onEntered={updateFeideTooltip}
                        >
                          {({ popper, ...overlayProps }) => {
                            feidePopperRef.current = popper;
                            return (
                              <Popover id="social-login-feide-popover" {...overlayProps}>
                                <Popover.Body className={styles.popover_text}>
                                  If you have any questions regarding access via Feide, please contact your administrator or email us at help@w3schools.com.
                                </Popover.Body>
                              </Popover>
                            );
                          }}
                        </Overlay>
                        <button
                          type="button"
                          className={styles.social_login_button}
                          onClick={() => handleSocialLoginClick(provider)}
                          onMouseEnter={() => setShowFeideTooltip(true)}
                          onMouseLeave={() => setShowFeideTooltip(false)}
                          onFocus={() => setShowFeideTooltip(true)}
                          onBlur={() => setShowFeideTooltip(false)}
                          ref={feideButtonRef}
                        >
                          <div className={styles.provider_text}>{SOCIAL_BUTTON_DISPLAY_NAMES[provider] ?? provider}</div>
                          <div className={styles.provider_logo} aria-hidden="true">
                            {providerLogoMap[provider]}
                          </div>
                        </button>
                      </React.Fragment>
                    ) : (
                      <button
                        key={index}
                        type="button"
                        className={styles.social_login_button}
                        onClick={() => handleSocialLoginClick(provider)}
                      >
                        <div className={styles.provider_text}>{SOCIAL_BUTTON_DISPLAY_NAMES[provider] ?? provider}</div>
                        <div className={styles.provider_logo} aria-hidden="true">
                          {providerLogoMap[provider]}
                        </div>
                      </button>
                    ),
                  )}
                </div>
                <p className={styles.or_text}>OR</p>
              </>
            </div>
          )}
          <div className={styles.content}>
            {view === LoginView.SIGNUP && (
              <SignUpForm
                setView={setView}
                email={email}
                setEmail={setEmail}
                password={password}
                setPassword={setPassword}
                setUsername={setUsername}
              />
            )}

            {view === LoginView.LOGIN && (
              <LoginForm
                setView={setView}
                email={email}
                setEmail={setEmail}
                password={password}
                setPassword={setPassword}
                setUsername={setUsername}
              />
            )}

            {view === LoginView.RESET_PASSWORD && (
              <div className={styles.reset_password}>
                <ResetPasswordForm setView={setView} />
              </div>
            )}

            {view === LoginView.RESET && (
              <div className={styles.reset}>
                <ChangePasswordForm setView={setView} />
              </div>
            )}

            {view === LoginView.VERIFICATION_PENDING && (
              <div className={styles.verification_pending}>
                <SignupVerifyEmail email={email} username={username} setView={setView} />
              </div>
            )}

            {view === LoginView.CONFIRM_NEW_PASSWORD && (
              <div className={styles.confirm_new_password}>
                <ConfirmWithNewPasswordForm setView={setView} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPanel;
